import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { Project, ProjectMetadataPatch } from '@/lib/api/client'
import {
  ProjectMetadataFields,
  EMPTY_METADATA,
  type MetadataValues,
} from './ProjectMetadataFields'

interface EditProjectDialogProps {
  project: Project | null
  isSaving: boolean
  onSave: (updates: ProjectMetadataPatch) => Promise<void>
  onClose: () => void
}

function metadataFromProject(project: Project): MetadataValues {
  return {
    companyType: project.companyType ?? null,
    productType: project.productType ?? null,
    market: project.market ?? null,
    industry: project.industry ?? '',
    targetRegion: project.targetRegion ?? null,
    clientName: project.clientName ?? '',
    status: project.status ?? 'active',
    tags: project.tags ?? [],
  }
}

export function EditProjectDialog({
  project,
  isSaving,
  onSave,
  onClose,
}: EditProjectDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [metadata, setMetadata] = useState<MetadataValues>(EMPTY_METADATA)
  const [error, setError] = useState('')

  useEffect(() => {
    if (project) {
      setName(project.name)
      setDescription(project.description ?? '')
      setMetadata(metadataFromProject(project))
      setError('')
    }
  }, [project])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!name.trim()) return setError('Name is required')
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        companyType: metadata.companyType,
        productType: metadata.productType,
        market: metadata.market,
        industry: metadata.industry.trim() || null,
        targetRegion: metadata.targetRegion,
        clientName: metadata.clientName.trim() || null,
        status: metadata.status,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project')
    }
  }

  return (
    <Dialog open={!!project} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-xl" style={{ maxWidth: '40rem' }}>
        <DialogHeader>
          <DialogTitle className="font-display">Edit project</DialogTitle>
          <DialogDescription>Update name, description, and portfolio metadata.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
          {error && (
            <div className="px-3 py-2 bg-danger-bg text-danger-fg text-sm rounded-lg">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="w-full h-9 px-3 bg-warm-50 border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Description{' '}
              <span className="text-text-muted normal-case font-normal">— optional</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-warm-50 border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15 resize-none"
              placeholder="What is this project about?"
              rows={2}
            />
          </div>

          <div className="pt-3 border-t border-border-subtle">
            <p className="text-xs text-text-muted mb-3 uppercase tracking-wide font-medium">
              Portfolio metadata
            </p>
            <ProjectMetadataFields values={metadata} onChange={setMetadata} showStatus />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Slug
            </label>
            <div className="flex items-center h-9 px-3 bg-warm-100 border border-border-subtle rounded-lg text-sm font-mono text-text-secondary">
              {project?.slug}
            </div>
            <p className="mt-1 text-xs text-text-muted">
              Used in URLs. The slug cannot be changed after creation.
            </p>
          </div>

          <DialogFooter className="!mt-6 gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
